import type { Task, TaskListScope } from "@asap/shared/schemas/task/crud";
import type { Ref } from "vue";
import { useToast } from "~/composables/useToast";
import { getTodayDate, toTaskDueDay } from "~/components/task/task-date";
import {
  TASK_GROUP_IDS,
  toTaskListQuery,
  useTasks,
  type TaskGroupId,
  type TaskSubjectFilter,
} from "~/components/task/composables/useTasks";

const STRIKE_THROUGH_DURATION_MS = 175;
const OPEN_BY_DEFAULT_GROUP_IDS: TaskGroupId[] = ["overdue", "today", "later"];

export type TaskGroupState = {
  id: TaskGroupId;
  color?: "red";
  count: number;
  items: Task[];
  isLoaded: boolean;
  isLoading: boolean;
  isOpen: boolean;
};

const createGroup = (id: TaskGroupId, color?: "red"): TaskGroupState => ({
  id,
  color,
  count: 0,
  items: [],
  isLoaded: false,
  isLoading: false,
  isOpen: false,
});

export const toOpenTaskGroupId = (dueDate: string): Exclude<TaskGroupId, "done"> => {
  const today = getTodayDate();
  const dueDay = toTaskDueDay(dueDate);
  if (dueDay < today) {
    return "overdue";
  }
  if (dueDay === today) {
    return "today";
  }
  return "later";
};

const compareCompletedAtDesc = (left: Task, right: Task) => {
  const leftCompletedAt = left.completed_at ?? "";
  const rightCompletedAt = right.completed_at ?? "";
  if (leftCompletedAt === rightCompletedAt) {
    return right.id.localeCompare(left.id);
  }
  return rightCompletedAt.localeCompare(leftCompletedAt);
};

const compareDueDateAsc = (left: Task, right: Task) => {
  if (left.due_date === right.due_date) {
    return left.created_at.localeCompare(right.created_at);
  }
  return left.due_date.localeCompare(right.due_date);
};

const waitForStrikeThrough = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, STRIKE_THROUGH_DURATION_MS);
  });

type UseTaskGroupsOptions = {
  scope: Readonly<Ref<TaskListScope>>;
  getSubject?: () => TaskSubjectFilter;
};

/**
 * State and actions behind the grouped task list (overdue / today / later / done),
 * shared by the tasks page and the entity detail pages.
 */
export const useTaskGroups = ({ scope, getSubject }: UseTaskGroupsOptions) => {
  const { t } = useI18n();
  const { listTasks, countTasksInGroup, completeTask, restoreTask } = useTasks();
  const { addToastError } = useToast();

  const taskAccordionGroups = reactive<Record<TaskGroupId, TaskGroupState>>({
    overdue: createGroup("overdue", "red"),
    today: createGroup("today"),
    later: createGroup("later"),
    done: createGroup("done"),
  });

  const completingTaskIds = ref(new Set<string>());
  const restoringTaskIds = ref(new Set<string>());
  let loadRequestId = 0;

  const groupList = computed(() => TASK_GROUP_IDS.map((groupId) => taskAccordionGroups[groupId]));

  const isTaskDisplayedAsDone = (task: Task) =>
    (task.status === "done" && !restoringTaskIds.value.has(task.id)) || completingTaskIds.value.has(task.id);

  const isTaskMutating = (taskId: string) => completingTaskIds.value.has(taskId) || restoringTaskIds.value.has(taskId);

  const markTaskCompleted = (taskId: string) => {
    const nextCompletingTaskIds = new Set(completingTaskIds.value);
    nextCompletingTaskIds.add(taskId);
    completingTaskIds.value = nextCompletingTaskIds;
  };

  const unmarkTaskCompleted = (taskId: string) => {
    const nextCompletingTaskIds = new Set(completingTaskIds.value);
    nextCompletingTaskIds.delete(taskId);
    completingTaskIds.value = nextCompletingTaskIds;
  };

  const markTaskRestored = (taskId: string) => {
    const nextRestoringTaskIds = new Set(restoringTaskIds.value);
    nextRestoringTaskIds.add(taskId);
    restoringTaskIds.value = nextRestoringTaskIds;
  };

  const unmarkTaskRestored = (taskId: string) => {
    const nextRestoringTaskIds = new Set(restoringTaskIds.value);
    nextRestoringTaskIds.delete(taskId);
    restoringTaskIds.value = nextRestoringTaskIds;
  };

  const insertCompletedTask = (completedTask: Task) => {
    const remainingDoneTasks = taskAccordionGroups.done.items.filter((task) => task.id !== completedTask.id);
    taskAccordionGroups.done.items = [completedTask, ...remainingDoneTasks].sort(compareCompletedAtDesc);
  };

  const synchronizeOpenTask = (openTask: Task) => {
    const targetGroup = taskAccordionGroups[toOpenTaskGroupId(openTask.due_date)];
    const remainingOpenTasks = targetGroup.items.filter((task) => task.id !== openTask.id);
    targetGroup.items = [...remainingOpenTasks, openTask].sort(compareDueDateAsc);
  };

  const loadGroup = async (groupId: TaskGroupId) => {
    const group = taskAccordionGroups[groupId];
    const requestedScope = scope.value;
    const requestId = loadRequestId;

    const shouldShowLoadingState = !group.isLoaded;
    if (shouldShowLoadingState) {
      group.isLoading = true;
    }

    try {
      const result = await listTasks(toTaskListQuery(groupId, requestedScope, getSubject?.()));
      if (scope.value !== requestedScope || requestId !== loadRequestId) {
        return;
      }

      group.items = groupId === "done" ? [...result.items] : [...result.items].sort(compareDueDateAsc);
      group.count = result.totalItems;
      group.isLoaded = true;
    } catch (error) {
      if (scope.value !== requestedScope || requestId !== loadRequestId) {
        return;
      }

      addToastError({ description: t("tasks.page.loadError") }, error);
    } finally {
      if (shouldShowLoadingState && scope.value === requestedScope && requestId === loadRequestId) {
        group.isLoading = false;
      }
    }
  };

  const onGroupOpen = (group: TaskGroupState, isOpen: boolean) => {
    group.isOpen = isOpen;
    if (isOpen && !group.isLoaded && !group.isLoading) {
      loadGroup(group.id);
    }
  };

  const onTaskDoneChange = async (group: TaskGroupState, task: Task, isDone: boolean) => {
    if (isTaskMutating(task.id)) {
      return;
    }

    const requestedScope = scope.value;
    const requestId = loadRequestId;
    const isStale = () => scope.value !== requestedScope || requestId !== loadRequestId;

    if (isDone) {
      if (task.status === "done") {
        return;
      }

      markTaskCompleted(task.id);

      try {
        const [completedTask] = await Promise.all([completeTask(task.id), waitForStrikeThrough()]);
        if (isStale()) {
          return;
        }

        group.items = group.items.filter((currentTask) => currentTask.id !== completedTask.id);
        group.count = Math.max(0, group.count - 1);
        taskAccordionGroups.done.count += 1;

        if (taskAccordionGroups.done.isOpen || taskAccordionGroups.done.isLoaded) {
          insertCompletedTask(completedTask);
        }
      } catch {
        // Toast is already shown by completeTask
      } finally {
        unmarkTaskCompleted(task.id);
      }
      return;
    }

    if (task.status !== "done") {
      return;
    }

    markTaskRestored(task.id);

    try {
      const [restoredTask] = await Promise.all([restoreTask(task.id), waitForStrikeThrough()]);
      if (isStale()) {
        return;
      }

      group.items = group.items.filter((currentTask) => currentTask.id !== restoredTask.id);
      group.count = Math.max(0, group.count - 1);

      const targetGroup = taskAccordionGroups[toOpenTaskGroupId(restoredTask.due_date)];
      targetGroup.count += 1;

      if (targetGroup.isOpen || targetGroup.isLoaded) {
        synchronizeOpenTask(restoredTask);
      }
    } catch {
      // Toast is already shown by restoreTask
    } finally {
      unmarkTaskRestored(task.id);
    }
  };

  const load = async () => {
    loadRequestId += 1;
    const requestId = loadRequestId;
    const requestedScope = scope.value;

    for (const groupId of TASK_GROUP_IDS) {
      const group = taskAccordionGroups[groupId];
      group.count = 0;
      group.items = [];
      group.isLoaded = false;
      group.isOpen = OPEN_BY_DEFAULT_GROUP_IDS.includes(groupId);
      group.isLoading = group.isOpen;
    }

    try {
      // Fetch only count for "done" group collapsed AND full data for others opened groups
      const [doneCount] = await Promise.all([
        countTasksInGroup("done", requestedScope, getSubject?.()),
        ...OPEN_BY_DEFAULT_GROUP_IDS.map((groupId) => loadGroup(groupId)),
      ]);
      if (requestId !== loadRequestId || scope.value !== requestedScope) {
        return;
      }
      taskAccordionGroups.done.count = doneCount;
    } catch (error) {
      if (requestId !== loadRequestId) {
        return;
      }
      addToastError({ description: t("tasks.page.loadError") }, error);
    }
  };

  return {
    taskAccordionGroups,
    groupList,
    isTaskDisplayedAsDone,
    loadGroup,
    load,
    onGroupOpen,
    onTaskDoneChange,
  };
};
